<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import HierarchyTreeEditor from './hierarchy-tree-editor.vue'
import FormMixin from '@lib/mixins/modal-form'
import ProductMixin from '~/mixins/product.mixin'

export default {
  name: 'CategoryHierarchy',
  components: {
    HierarchyTreeEditor,
  },
  mixins: [FormMixin, ProductMixin],
  props: {
    id: {
      type: String,
      default: '',
    },
    category: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      backFromEditingRoute: {
        name: 'product-category-tree',
        params: { id: this.id },
      },
      backFromCreatingRoute: {
        name: 'product-category-trees',
      },
      form: {
        name: '',
        type: 'groups',
        tree: [],
      },
      loading: false,
    }
  },
  computed: {
    ...mapState('category', ['tree']),
    ...mapGetters('category', ['dynamicRules']),
    rules() {
      const rules = {
        name: [
          { required: true },
        ],
        type: [
          { required: true, trigger: 'blur' },
        ],
        tree: this.dynamicRules,
      }
      return rules
    },
    isEdit() {
      return Boolean(this.id)
    },
  },
  watch: {
    'form.type'(type) {
      this.switchType(type)
    },
    tree: {
      handler(tree) {
        this.form.tree = tree
      },
      deep: true,
    },
  },
  created() {
    if (this.category) {
      this.loading = true
      this.form.name = this.category.name
      const depth = this.getDepth(this.category)
      switch (depth) {
        case 3:
          this.form.type = 'groups'
          break
        case 2:
          this.form.type = 'tiers'
          break
        default:
          this.form.type = 'categories'
          break
      }
      this.$nextTick(() => {
        this.storeTree(this.category.categories)
        this.form.tree = this.tree
        this.loading = false
      })
    } else {
      this.switchType(this.form.type)
    }

    this.$nextTick(() => this.cloneOriginalData())
  },
  methods: {
    ...mapActions('category', ['switchType', 'storeTree']),
    ...mapActions('categoriesTree', ['createCategoryTree', 'updateCategoryTree']),
    submit() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          let response
          if (this.category) {
            response = await this.updateCategoryTree({
              ...this.category,
              name: this.form.name,
              categories: this.tree,
            })
          } else {
            response = await this.createCategoryTree({
              name: this.form.name,
              categories: this.tree,
            })
          }

          this.$notifier({
            response,
            successMsg: `Category tree has been successfully ${this.category ? 'updated' : 'created'}`,
          }).then(success => {
            this.$router.push({
              name: 'product-category-tree',
              params: { id: success.id, allow: true },
            })
          })
        }
      })
    },
    cancel() {
      this.$router.push(
        this.isEdit
          ? this.backFromEditingRoute
          : this.backFromCreatingRoute
      )
    },
    getDepth(item, depth = 0) {
      const items = item.categories || item.items
      if (items.length) {
        return this.getDepth(items[0], depth + 1)
      }
      return depth
    },
  },
}
</script>

<template>
  <main-layout
    :title="`${isEdit ? 'Edit' : 'Create'} Product Category Tree`"
    :back="isEdit ? backFromEditingRoute : backFromCreatingRoute"
    :loading="loading"
  >
    <data-box>
      <div :class="$style.title">
        Category Tree Configurations
      </div>
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="100%"
        label-position="top"
      >
        <div
          :class="$style.form"
        >
          <el-form-item
            label="Product Category Tree Name"
            prop="name"
          >
            <el-input v-model="form.name" />
          </el-form-item>
          <el-form-item
            prop="type"
          >
            <el-radio-group
              v-model="form.type"
              :class="$style.radioGroup"
            >
              <el-radio label="categories">
                Single Level (Just Categories)
              </el-radio>
              <el-radio label="tiers">
                2 Levels (Tiers + Categories)
              </el-radio>
              <el-radio label="groups">
                3 Levels (Groups + Tiers + Categories)
              </el-radio>
            </el-radio-group>
          </el-form-item>
        </div>

        <div :class="$style.title">
          Category Tree Settings
        </div>

        <hierarchy-tree-editor
          v-model="tree"
          :type="form.type"
          @cancel="cancel"
          @submit="submit"
        />

        <!-- NOTE Remove when make tree working again -->
        <!-- <div :class="$style.buttons">
          <el-button
            type="primary"
            @click="submit"
          >
            Save
          </el-button>
        </div> -->
        <!-- END OF NOTE -->
      </el-form>
    </data-box>
  </main-layout>
</template>

<style lang="scss" module>
.title {
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
  font-weight: 600;
}

.form {
  width: 350px;
}

.radioGroup {
  label {
    margin-bottom: 1rem;
  }
}
</style>
